import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { OdataValueHelperInterface } from '../../interfaces/odata-value-helper.interface';
import { OdataResponse } from '../../interfaces/online-only.interface';
import { ODataValueHelperModel, ValueHelperModel } from '../../models/low-level/value-helper.model';
import { UriUtilitiesService } from '../../services/uri-utilities/uri-utilities.service';
import { isDefined } from '../../utilities/is-defined';
import { ODataUtilities } from '../../utilities/odata-utilities';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import { ValueHelperState } from '../../state/value-helper';

@Injectable({
    providedIn: 'root',
})
export class OdataValueHelperService implements OdataValueHelperInterface {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly http: HttpClient,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
    ) {}

    /**
     * Performs the remote http transaction using the configuration parameters from the authenticationService and
     * the ConfigInterfaceService settings. Builds the query string and encapsulates the OData call. Returns a
     * promise and resolves it or just passes the unsuccessful promise through.
     */
    public getValueHelpers(): Observable<ValueHelperModel[]> {
        const query = this.userMasterService.setSalesOrgAndPlantForUrl(
            this.uriUtilities.getUriForContent() + ENDPOINTS.ValueHelpers,
        );

        return this.http.get<OdataResponse<ODataValueHelperModel>>(query).pipe(
            map((response) => {
                return ODataUtilities.transformResponseDataToArray(response.d)
                    .map((value: ODataValueHelperModel) => ValueHelperModel.fromOData(value))
                    .filter(isDefined);
            }),
            catchError((err) => {
                this.log.error(
                    'getValueHelpers() - request error:  ' + JSON.stringify(err, Object.getOwnPropertyNames(err)),
                );
                return throwError(err);
            }),
        );
    }

    public getValueHelpersState(): Observable<ValueHelperState[]> {
        const query = this.userMasterService.setSalesOrgAndPlantForUrl(
            this.uriUtilities.getUriForContent() + ENDPOINTS.ValueHelpers,
        );

        return this.http.get<OdataResponse<ODataValueHelperModel>>(query).pipe(
            map((response) => {
                return ODataUtilities.transformResponseDataToArray(response.d)
                    .filter((vh) => vh.DescTxt !== '?')
                    .map((value: ODataValueHelperModel) => ({
                        id: value.ID,
                        sortField: value.SortField,
                        code: value.Code,
                        codeLevel: value.CodeLevel,
                        descText: value.DescTxt,
                        descTextLong: value.DescTxtLong,
                        werks: value.Werks,
                        vkorg: value.Vkorg,
                        contractCharacteristic: value.ContractCharacteristic,
                        contractCharacteristicValue: value.ContractCharacteristicValue,
                    }))
                    .filter(isDefined);
            }),
            catchError((err) => {
                this.log.error(
                    'getValueHelpers() - request error:  ' + JSON.stringify(err, Object.getOwnPropertyNames(err)),
                );
                return throwError(err);
            }),
        );
    }
}
